import React, { useState, useEffect, useMemo } from 'react';
import Loading from '../../Common/Loading';
import popup_bg1 from '../../Common/Images/ImagesForAlert/popup_bg1.png';
import alert_close_n from '../../Common/Images/ImagesForAlert/alert_close_n.png';
import alert_close_o from '../../Common/Images/ImagesForAlert/alert_close_o.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface PurchaseRecord {
    Surname: string;
    FirstName: string;
    Country: string;
    Phone: string;
    Email: string;
    Address: string;
    SchoolCode: string;
    FamilyCode: string;
    Login: string;
}

interface Props {
    avatarTitle: string;
    totalPurchases: number;
    onClose: () => void;
    showWarning: (msg: string) => void;
    showConfirm: (msg: string, onConfirm: () => void) => void;
}

const EL_ViewAvatarPurchases: React.FC<Props> = ({ avatarTitle, totalPurchases, onClose, showWarning, showConfirm }) => {
    const [loading, setLoading] = useState(false);
    // ALL purchases fetched from backend only once
    const [allPurchases, setAllPurchases] = useState<PurchaseRecord[]>([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(15);
    const [sortField, setSortField] = useState<keyof PurchaseRecord | null>(null);
    const [sortAsc, setSortAsc] = useState(true);
    const sortedPurchases = useMemo(() => {
        let data = [...allPurchases];

        if (sortField) {
            data.sort((a, b) => {
                const aValue = a[sortField] ?? '';
                const bValue = b[sortField] ?? '';

                const aString = String(aValue).toLowerCase();
                const bString = String(bValue).toLowerCase();

                if (aString < bString) return sortAsc ? -1 : 1;
                if (aString > bString) return sortAsc ? 1 : -1;

                return 0;
            });
        }

        return data;
    }, [allPurchases, sortField, sortAsc]);

    const totalPages = Math.ceil(sortedPurchases.length / pageSize);

    const paginatedPurchases = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;

        return sortedPurchases.slice(start, end);
    }, [sortedPurchases, currentPage, pageSize]);

    const handleSort = (field: keyof PurchaseRecord) => {
        if (sortField === field) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            setSortAsc(true);
        }
    };

    useEffect(() => {
        if (avatarTitle) {
            fetchPurchases();
        }
    }, [avatarTitle]);

    const fetchPurchases = async () => {
        setLoading(true);

        try {
            const encodedTitle = encodeURIComponent(avatarTitle);

            // IMPORTANT:
            // Do NOT send page/page_size because backend returns ALL records.
            const res = await fetch(
                `${API_BASE_URL}api/manage_avatars/purchases/?avatar_title=${encodedTitle}`
            );

            if (!res.ok) {
                showWarning("Failed to load purchases");
                return;
            }

            const data = await res.json();

            if (Array.isArray(data)) {
                // Store EVERYTHING returned by backend.
                setAllPurchases(data);

                // Always start from page 1.
                setCurrentPage(1);
            } else if (data.results && Array.isArray(data.results)) {
                // In case backend is changed later to return DRF pagination.
                setAllPurchases(data.results);
                setCurrentPage(1);
            } else {
                setAllPurchases([]);
                setCurrentPage(1);
            }

        } catch (e) {
            console.error("Error fetching purchases:", e);
            showWarning("Network Error");
        } finally {
            setLoading(false);
        }
    };
    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            // NO API CALL HERE.
            // We already have all records in allPurchases.
            setCurrentPage(newPage);
        }
    };

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },
        modal: {
            backgroundColor: 'white',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column' as const,
            border: 'none',
            overflow: 'hidden',
            fontFamily: 'Arial, sans-serif',
            fontSize: '9pt'
        },
        popupHeader: {
            backgroundImage: `url(${popup_bg1})`,
            backgroundRepeat: 'repeat-x',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #000',
            height: '30px',
            color: 'white',
            fontFamily: 'Calibri, Arial, sans-serif',
            fontSize: '14pt',
            fontWeight: 'bold' as const,
            paddingLeft: '10px',
            flexShrink: 0
        },
        maroonHeader: {
            backgroundColor: 'Maroon',
            color: 'White',
            padding: '5px',
            fontSize: '14pt',
            fontFamily: 'Arial',
            fontWeight: 'bold' as const,
            textAlign: 'center' as const,
            flexShrink: 0
        },
        infoContainer: {
            padding: '10px 25px',
            fontSize: '12pt',
            fontFamily: 'Arial',
            color: 'black',
            flexShrink: 0
        },
        gridWrapper: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'hidden',
            padding: '0 10px'
        },
        gridContainer: {
            flex: 1,
            overflow: 'auto',
            border: '1px solid #80A0FF',
            borderBottom: 'none'
        },
        gridHeader: {
            backgroundColor: '#C7DDFD',
            color: '#2859ac',
            fontWeight: 'normal' as const,
            position: 'sticky' as const,
            top: 0,
            borderBottom: '1px solid #80A0FF',
            borderRight: '1px solid #80A0FF',
            fontSize: '10pt',
            fontFamily: 'Trebuchet MS',
            zIndex: 1
        },
        th: {
            padding: '8px',
            fontSize: '10pt',
            borderRight: '1px solid #80A0FF',
            textAlign: 'left' as const,
            whiteSpace: 'nowrap' as const,
            fontWeight: 'normal' as const,

        },
        td: {
            padding: '8px',
            borderRight: '1px solid #80A0FF',
            // borderBottom: '1px solid #80A0FF',
            fontSize: '10pt',
            fontFamily: 'Arial',
            color: '#000000',
            whiteSpace: 'nowrap' as const
        },
        paginationBar: {
            backgroundColor: '#C7DDFD',
            border: '1px solid #80A0FF',
            borderTop: '2px solid #80A0FF',
            padding: '8px',
            textAlign: 'center' as const,
            fontSize: '10pt',
            fontFamily: 'Arial',
            color: 'blue',
            fontWeight: 'bold' as const,
            flexShrink: 0
        },
        navLink: {
            textDecoration: 'underline',
            cursor: 'pointer',
            margin: '0 4px',
            userSelect: 'none' as const
        },
        disabledLink: {
            color: 'gray',
            textDecoration: 'none',
            cursor: 'default',
            margin: '0 4px',
            userSelect: 'none' as const
        },
        footer: {
            padding: '10px 22px',
            textAlign: 'left' as const,
            flexShrink: 0,
            backgroundColor: 'white'
        },
        closeButton: {
            width: '120px',
            height: '25px',
            cursor: 'pointer',
            fontFamily: 'Arial',
            fontSize: '10pt'
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.popupHeader}>
                    Avatar Purchases
                    <span>&nbsp;</span>
                    <img
                        src={alert_close_n}
                        alt="Close"
                        onClick={onClose}
                        style={{ cursor: 'pointer' }}
                        onMouseOver={(e) => e.currentTarget.src = alert_close_o}
                        onMouseOut={(e) => e.currentTarget.src = alert_close_n}
                    />
                </div>

                <div style={styles.maroonHeader}>
                    Avatar Purchases
                </div>

                <div style={styles.infoContainer}>
                    <table style={{ fontFamily: 'Arial', fontSize: '12pt' }}>
                        <tbody>
                            {/* <div style={{height:"2px", backgroundColor:"#0078d4", width:"100vw"}}></div> */}
                            <tr style={{ padding: "5px" }}>
                                <td style={{ color: "black" }}><b>Avatar Name:&nbsp;</b>{avatarTitle}</td>

                            </tr>
                            {/* <div style={{height:"2px", backgroundColor:"#0078d4", width:"100vw"}}></div> */}
                            <tr>
                                <td style={{ color: "black" }}><b>Total Purchases:&nbsp;</b>{totalPurchases}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={{ height: '10px' }}></div>

                <div style={styles.gridWrapper}>
                    <div style={styles.gridContainer}>
                        {loading ? <Loading /> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #80A0FF', }}>
                                <thead style={styles.gridHeader}>
                                    <tr>
                                        <th style={{ ...styles.th, width: '30px', borderLeft: '1px solid #a3c0e8' }}>#</th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('Surname')}>
                                            Surname {sortField === 'Surname' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('FirstName')}>
                                            First Name {sortField === 'FirstName' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('Country')}>
                                            Country {sortField === 'Country' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('Phone')}>
                                            Phone {sortField === 'Phone' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('Email')}>
                                            Email {sortField === 'Email' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('Address')}>
                                            Address {sortField === 'Address' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('SchoolCode')}>
                                            SchoolCode {sortField === 'SchoolCode' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('FamilyCode')}>
                                            FamilyCode {sortField === 'FamilyCode' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                        <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('Login')}>
                                            Login {sortField === 'Login' ? (sortAsc ? '▲' : '▼') : ''}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedPurchases.length > 0 ? (
                                        paginatedPurchases.map((row, idx) => (
                                            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f4faff' }}>
                                                <td style={styles.td}>{((currentPage - 1) * pageSize) + idx + 1}</td>
                                                <td style={styles.td}>{row.Surname}</td>
                                                <td style={styles.td}>{row.FirstName}</td>
                                                <td style={styles.td}>{row.Country}</td>
                                                <td style={styles.td}>{row.Phone}</td>
                                                <td style={styles.td}>{row.Email}</td>
                                                <td style={styles.td}>{row.Address}</td>
                                                <td style={styles.td}>{row.SchoolCode}</td>
                                                <td style={styles.td}>{row.FamilyCode}</td>
                                                <td style={styles.td}>{row.Login}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={10} style={{ ...styles.td, textAlign: 'center', padding: '20px' }}>
                                                No records found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div style={styles.paginationBar}>
                        <span
                            style={currentPage > 1 ? styles.navLink : styles.disabledLink}
                            onClick={() => currentPage > 1 && handlePageChange(1)}
                        >
                            &lt;&lt;
                        </span>
                        <span
                            style={currentPage > 1 ? styles.navLink : styles.disabledLink}
                            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                        >
                            &lt;
                        </span>

                        <span style={{ margin: '0 10px', color: 'blue', textDecoration: 'none' }}>
                            {currentPage} of {totalPages || 1}
                        </span>

                        <span
                            style={currentPage < totalPages ? styles.navLink : styles.disabledLink}
                            onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                        >
                            &gt;
                        </span>
                        <span
                            style={currentPage < totalPages ? styles.navLink : styles.disabledLink}
                            onClick={() => currentPage < totalPages && handlePageChange(totalPages)}
                        >
                            &gt;&gt;
                        </span>
                    </div>
                </div>

                <div style={styles.footer}>
                    <button onClick={onClose} style={styles.closeButton}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default EL_ViewAvatarPurchases;